import PropTypes from 'prop-types';
import React, { useEffect, memo } from 'react';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import { removeRuntimeError, addRuntimeError } from '../actions/applicationErrors';
import { usePopupContext } from '../contexts/PopupContext';

/**
 * Wrapper that displays Runtime's errors.
 */
const RuntimeErrorDisplayWrapper = ({
  children,
  global = false,
  errors,
  onDevOnly = false,
  addRuntimeError,
  removeRuntimeError,
}) => {
  const { hasPopup, openPopup: openPopupContext, onClosePopup } = usePopupContext();

  useEffect(() => {
    if (global) {
      const handleError = error => {
        if (
          error.message === 'ResizeObserver loop completed with undelivered notifications.' ||
          error.message === 'ResizeObserver loop limit exceeded'
        ) {
          error.stopImmediatePropagation();
        } else {
          addRuntimeError({ error });
        }
      };

      window.addEventListener('error', handleError);

      return () => {
        window.removeEventListener('error', handleError);
      };
    }
  }, [global, addRuntimeError]);

  useEffect(() => {
    if (errors.size === 0 || hasPopup()) return;

    const error = errors.get(0);

    if (onDevOnly && process.env.NODE_ENV === 'production') {
      removeRuntimeError({ error });

      return;
    }

    openPopup(error);
  }, [errors, onDevOnly, removeRuntimeError, hasPopup]);

  const openPopup = error => {
    openPopupContext('RuntimeErrorPopup', {
      error,
    });

    onClosePopup(() => {
      handleClose(error);
    });
  };

  const handleClose = error => {
    removeRuntimeError({ error });
  };

  return <div className="full-height">{children}</div>;
};

RuntimeErrorDisplayWrapper.propTypes = {
  addRuntimeError: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  errors: PropTypes.object.isRequired,
  global: PropTypes.bool,
  onDevOnly: PropTypes.bool,
  removeRuntimeError: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  errors: state.applicationErrors.get('runtime'),
});

export { RuntimeErrorDisplayWrapper };
export default connect(
  mapStateToProps,
  { removeRuntimeError, addRuntimeError },
  mergeProps,
  connectOptions,
)(memo(RuntimeErrorDisplayWrapper));
